import { useState, useEffect, useMemo, useRef } from "react";
import { Loader2, FileCheck, ChevronDown, ChevronLeft, ChevronRight, AlertCircle, CheckCircle2, FileSearch, Calendar, DollarSign, User, Paperclip, Tag, Pencil, Trash2 } from "lucide-react";
import { useDossierStore } from "../../store/dossierStore";
import {
  fetchTransactionsForCompta,
  normalizeJustificatifStatut,
  updateTransactionJustificatifCompta,
  type TransactionWithLignes,
} from "../../lib/transactions";
import { supabase } from "../../lib/supabase";
import { genererEcritureTransactionAttenteBancaire } from "../../lib/comptable";
import { formatCompte8 } from "../../lib/comptable/formatCompte";
import { fetchTransactionIdsWithFactureVenteRapprochee } from "../../lib/facturation";
import TransactionComptaDrawer from "../../components/compta/transaction/TransactionComptaDrawer";
import { formatMontantFCFA } from "@/lib/formatMontant";

const fmt = (n: number) => formatMontantFCFA(n);
const fmtDate = (d: string) => d ? new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : "—";
const fmtDateCourt = (d: string) => (d ? new Date(d).toLocaleDateString("fr-FR") : "—");

type OperateurId = "tous" | "orange_money" | "mtn" | "wave" | "banque" | "manuel";
const OPERATEURS: { id: OperateurId; label: string; logo?: string; icon?: string }[] = [
  { id: "tous", label: "Tous les comptes" },
  { id: "banque", label: "Banque traditionnelle", icon: "BQ" },
  { id: "orange_money", label: "Orange Money", logo: "/logo-orangemoney.png" },
  { id: "mtn", label: "MTN MoMo", logo: "/logo-momo.png" },
  { id: "wave", label: "Wave", logo: "/logo-wave.png" },
  { id: "manuel", label: "Saisie manuelle", icon: "M" },
];

const STATUT_FILTERS = [
  { id: "a_traiter" as const, label: "À traiter", icon: AlertCircle },
  { id: "pre_traitee" as const, label: "Pré-traitée", icon: FileSearch },
  { id: "traitee" as const, label: "Traitée", icon: CheckCircle2 },
  { id: "demande_comptable" as const, label: "Demande comptable", icon: FileSearch },
];

const SECONDARY_FILTERS = [
  { id: "montant" as const, label: "Montant", icon: DollarSign },
  { id: "contrepartie" as const, label: "Tiers", icon: User },
  { id: "avec_piece" as const, label: "Pièce justificative", icon: Paperclip },
  { id: "sans_piece" as const, label: "Sans pièce", icon: Tag },
  { id: "date" as const, label: "Date", icon: Calendar },
];

type CompteOption = {
  numero: string;
  libelle: string;
};

type LigneEcritureRow = {
  ecriture_id: string;
  compte_numero: string;
  compte_libelle: string;
  debit: number;
  credit: number;
};

/** Comptes trésorerie (banque, caisse, mobile money) — hors 471. */
function isCompteTresoreriePrincipal(numero: string): boolean {
  const p = String(numero ?? "").trim();
  if (!p || /^471/.test(p)) return false;
  if (/^52\d*/.test(p)) return true;
  if (/^55\d/.test(p)) return true;
  if (/^57\d/.test(p)) return true;
  return false;
}

/**
 * Contrepartie : ligne opposée à la trésorerie (débit banque → crédit hors tréso, etc.).
 */
function pickContrepartieDepuisLignesEcriture(lignes: LigneEcritureRow[]): CompteOption | null {
  if (!lignes.length) return null;
  const tresoLine = lignes.find(
    (l) => isCompteTresoreriePrincipal(l.compte_numero) && ((Number(l.debit) || 0) > 0 || (Number(l.credit) || 0) > 0)
  );
  if (!tresoLine) {
    const withAmt = lignes.filter((l) => (Number(l.debit) || 0) > 0 || (Number(l.credit) || 0) > 0);
    if (withAmt.length === 2) {
      const non471 = withAmt.find((l) => !/^471/.test(String(l.compte_numero).trim()));
      if (non471)
        return {
          numero: String(non471.compte_numero).trim(),
          libelle: String(non471.compte_libelle || "").trim(),
        };
    }
    return null;
  }
  const d = Number(tresoLine.debit) || 0;
  const c = Number(tresoLine.credit) || 0;
  if (d > 0) {
    const opp = lignes.find(
      (l) => (Number(l.credit) || 0) > 0 && !isCompteTresoreriePrincipal(l.compte_numero)
    );
    if (opp) return { numero: String(opp.compte_numero).trim(), libelle: String(opp.compte_libelle || "").trim() };
  }
  if (c > 0) {
    const opp = lignes.find(
      (l) => (Number(l.debit) || 0) > 0 && !isCompteTresoreriePrincipal(l.compte_numero)
    );
    if (opp) return { numero: String(opp.compte_numero).trim(), libelle: String(opp.compte_libelle || "").trim() };
  }
  return null;
}

type ContrepartieSourceGroup = "charges" | "produits" | "fournisseurs" | "clients" | "autres";

type ContrepartieCatalogOption = CompteOption & {
  group: ContrepartieSourceGroup;
  searchLower: string;
};

const GROUPE_CONTREPARTIE_LABEL: Record<ContrepartieSourceGroup, string> = {
  charges: "Charges (6xx)",
  produits: "Produits (7xx)",
  fournisseurs: "Fournisseurs",
  clients: "Clients",
  autres: "Autres",
};

const GROUPE_CONTREPARTIE_ORDER: ContrepartieSourceGroup[] = ["charges", "produits", "fournisseurs", "clients", "autres"];

const MAX_CONTREPARTIE_DROPDOWN = 20;

type SyscohadaCompteJsonRow = {
  value?: unknown;
  label?: unknown;
  name?: unknown;
  classe?: unknown;
  level?: unknown;
  searchText?: unknown;
};

function buildContrepartieCatalogFromSyscohadaJson(rows: SyscohadaCompteJsonRow[]): ContrepartieCatalogOption[] {
  const out: ContrepartieCatalogOption[] = [];
  const seen = new Set<string>();
  const push = (o: ContrepartieCatalogOption) => {
    const k = `${o.group}:${o.numero}`;
    if (seen.has(k)) return;
    seen.add(k);
    out.push(o);
  };
  for (const raw of rows) {
    const classe = Number(raw.classe);
    const level = Number(raw.level ?? 0);
    const val = String(raw.value ?? "").trim();
    if (!val || !/^\d+$/.test(val)) continue;
    const name = String(raw.name ?? "").trim();
    const label = String(raw.label ?? "").trim();
    const lib =
      name ||
      label
        .replace(new RegExp(`^${val}\\s*-\\s*`, "i"), "")
        .trim() ||
      label ||
      val;
    const st = String(raw.searchText ?? "").toLowerCase();
    const n8 = formatCompte8(val) || val;
    const searchLower = `${val.toLowerCase()} ${n8.toLowerCase()} ${lib.toLowerCase()} ${st}`.replace(/\s+/g, " ").trim();
    if (classe === 6) {
      push({ numero: n8, libelle: lib, group: "charges", searchLower });
    } else if (classe === 7) {
      push({ numero: n8, libelle: lib, group: "produits", searchLower });
    } else if (classe >= 1 && classe <= 5 && level >= 3) {
      push({ numero: n8, libelle: lib, group: "autres", searchLower });
    }
  }
  return out;
}

function filterContrepartieCatalog(
  catalog: ContrepartieCatalogOption[],
  q: string,
  max: number
): ContrepartieCatalogOption[] {
  const qt = q.trim().toLowerCase();
  if (!qt) {
    const per = Math.max(1, Math.ceil(max / GROUPE_CONTREPARTIE_ORDER.length));
    const acc: ContrepartieCatalogOption[] = [];
    for (const g of GROUPE_CONTREPARTIE_ORDER) {
      const fromG = catalog.filter((o) => o.group === g);
      for (let i = 0; i < fromG.length && acc.filter((x) => x.group === g).length < per && acc.length < max; i++) {
        acc.push(fromG[i]);
      }
    }
    return acc;
  }
  return [...catalog]
    .filter((o) => o.searchLower.includes(qt))
    .sort((a, b) => {
      const ia = GROUPE_CONTREPARTIE_ORDER.indexOf(a.group);
      const ib = GROUPE_CONTREPARTIE_ORDER.indexOf(b.group);
      if (ia !== ib) return ia - ib;
      return a.numero.localeCompare(b.numero);
    })
    .slice(0, max);
}

function groupedContrepartieRows(options: ContrepartieCatalogOption[]) {
  const rows: Array<{ kind: "header"; label: string } | { kind: "item"; opt: ContrepartieCatalogOption }> = [];
  let last: ContrepartieSourceGroup | null = null;
  for (const opt of options) {
    if (opt.group !== last) {
      last = opt.group;
      rows.push({ kind: "header", label: GROUPE_CONTREPARTIE_LABEL[opt.group] });
    }
    rows.push({ kind: "item", opt });
  }
  return rows;
}

/** Écriture transaction : création attente si besoin, remplacement 471, statut justifiée. */
async function persistContrepartieTransaction(txId: string, c: CompteOption): Promise<void> {
  let { data: ecriture, error: ecritureErr } = await supabase
    .from("ecritures_comptables")
    .select("id")
    .eq("source_type", "transaction")
    .eq("source_id", txId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (ecritureErr) throw ecritureErr;

  let ecritureId = ecriture?.id ? String(ecriture.id) : null;
  if (!ecritureId) {
    const created = await genererEcritureTransactionAttenteBancaire(txId);
    ecritureId = created ? String(created) : null;
    if (!ecritureId) {
      throw new Error("Impossible de créer l'écriture d'attente (transaction non éligible ou sans dossier).");
    }
  }

  const numero8 = formatCompte8(String(c.numero ?? "").trim()) || String(c.numero ?? "").trim();
  const { data: updatedLignes, error: ligneErr } = await supabase
    .from("ecritures_comptables_lignes")
    .update({
      compte_numero: numero8,
      compte_libelle: c.libelle,
    })
    .eq("ecriture_id", ecritureId)
    .like("compte_numero", "471%")
    .select("id");
  if (ligneErr) throw ligneErr;
  if (!updatedLignes?.length) {
    throw new Error("Aucune ligne d'attente (471) à remplacer sur cette écriture.");
  }

  const { error: txErr } = await supabase.from("transactions").update({ statut_justification: "justifiee" }).eq("id", txId);
  if (txErr) throw txErr;
}

/** Affichage colonne contrepartie : numéro seul (8 chiffres OHADA si applicable). */
function afficheNumeroContrepartie(c: Pick<CompteOption, "numero"> | null | undefined): string {
  const raw = String(c?.numero ?? "").trim();
  if (!raw) return "—";
  return formatCompte8(raw) || raw;
}

function journalBadgeForTx(tx: TransactionWithLignes): "BQ" | "CA" | "OD" {
  if (tx.operateur === "banque") return "BQ";
  if (tx.operateur === "manuel") return "OD";
  return "CA";
}

function extractContrepartie(libelle: string | null): string {
  if (!libelle?.trim()) return "—";
  const l = libelle.trim();
  const prefixes = ["Prélèvement ", "Paiement ", "Virement à ", "Virement de ", "Transfert ", "Paiement reçu de "];
  for (const p of prefixes) {
    if (l.toLowerCase().startsWith(p.toLowerCase())) return l.slice(p.length).trim() || "—";
  }
  return l.length > 35 ? l.slice(0, 32) + "…" : l;
}

function mapToDrawerTransaction(
  r: TransactionWithLignes,
  txIdsVente: Set<string>,
  txIdsAchat: Set<string>
) {
  const raw = r as { mode_traitement?: string };
  const context = txIdsAchat.has(r.id)
    ? ("achat" as const)
    : txIdsVente.has(r.id)
      ? ("vente_encaissement" as const)
      : ("direct" as const);
  return {
    id: r.id,
    date: r.date ?? "",
    libelle: r.libelle ?? "",
    montantTTC: Math.abs(Number(r.montant_ttc) || 0),
    type: (r.type === "entree" || r.type === "sortie" ? r.type : "entree") as "entree" | "sortie",
    statut: r.statut,
    statut_justification: r.statut_justification ?? undefined,
    suggestedTier: undefined as string | undefined,
    modeTraitement: (raw.mode_traitement === "facture" || raw.mode_traitement === "tresorerie_directe" ? raw.mode_traitement : undefined) as "facture" | "tresorerie_directe" | undefined,
    piece_nom: r.piece_nom ?? null,
    piece_url: r.piece_url ?? null,
    piece_type: r.piece_type ?? null,
    piece_taille: r.piece_taille ?? null,
    justificatif_requis: (r as { justificatif_requis?: boolean | null }).justificatif_requis !== false,
    justificatif_statut: normalizeJustificatifStatut(
      (r as { justificatif_statut?: string | null }).justificatif_statut ?? undefined
    ),
    context,
  };
}

function statutAfficheTransaction(
  tx: TransactionWithLignes,
  contrepartieSelectionParTx: Record<string, { numero: string; libelle: string }>,
  txHas471: Record<string, boolean>,
  txIdsAchat: Set<string>,
  txIdsCommentaires: Set<string>
): "a_traiter" | "pre_traitee" | "traitee" | "demande_comptable" {
  const modeTx = (tx as { mode_traitement?: string | null }).mode_traitement;
  const justifTx = normalizeJustificatifStatut(
    (tx as { justificatif_statut?: string | null }).justificatif_statut ?? undefined
  );
  const hasPiece = !!(tx.piece_nom || tx.piece_url);
  const selected = contrepartieSelectionParTx[tx.id];
  const fromLigne = (tx.transaction_lignes || [])[0];
  const fromFactAchat = (tx as { facture_achat_charge_compte?: { numero?: string | null } | null }).facture_achat_charge_compte;
  const compteNumero = String(selected?.numero ?? fromLigne?.compte_numero ?? fromFactAchat?.numero ?? "").trim();
  const hasContrepartie = compteNumero.length > 0 && !/^471/.test(compteNumero);
  const has471 = txHas471[tx.id] === true;
  const factureAchatLiee = txIdsAchat.has(tx.id);
  const hasCommentaire = txIdsCommentaires.has(tx.id);
  const estTraitee = tx.statut_justification === "justifiee" || tx.statut === "affectee";
  const ecriturePropre = !has471;

  if (hasCommentaire) return "demande_comptable";
  if (modeTx === "tresorerie_directe" && justifTx === "en_attente") return "demande_comptable";
  if ((hasContrepartie && hasPiece) || (estTraitee && ecriturePropre)) return "traitee";
  if ((hasContrepartie && !hasPiece) || (factureAchatLiee && has471)) return "pre_traitee";
  return "a_traiter";
}

export default function TransactionsCompta() {
  const { activeDossier } = useDossierStore();
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState<TransactionWithLignes[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [bulkJustifLoading, setBulkJustifLoading] = useState(false);
  const [comptaBulkToast, setComptaBulkToast] = useState<string | null>(null);
  const [comptaBulkToastType, setComptaBulkToastType] = useState<"success" | "error">("success");
  const [txIdsVente, setTxIdsVente] = useState<Set<string>>(new Set());
  const [txIdsAchat, setTxIdsAchat] = useState<Set<string>>(new Set());
  const [txHas471, setTxHas471] = useState<Record<string, boolean>>({});
  const [txHasEcritureComptable, setTxHasEcritureComptable] = useState<Record<string, boolean>>({});
  const [txContrepartieEcriture, setTxContrepartieEcriture] = useState<Record<string, CompteOption | null>>({});
  const [txIdsCommentaires, setTxIdsCommentaires] = useState<Set<string>>(new Set());
  const [sourceOpen, setSourceOpen] = useState(false);
  const [selectedSource, setSelectedSource] = useState<OperateurId>("tous");
  const [statutFilter, setStatutFilter] = useState<"a_traiter" | "pre_traitee" | "traitee" | "demande_comptable" | null>(null);
  const [secondaryFilter, setSecondaryFilter] = useState<"montant" | "contrepartie" | "avec_piece" | "sans_piece" | "date" | null>("sans_piece");
  const [montantExact, setMontantExact] = useState("");
  const [montantMin, setMontantMin] = useState("");
  const [montantMax, setMontantMax] = useState("");
  const [montantMode, setMontantMode] = useState<"exact" | "between">("exact");
  const [contrepartieSearch, setContrepartieSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [contrepartieOpenTxId, setContrepartieOpenTxId] = useState<string | null>(null);
  const [contrepartieSearchInput, setContrepartieSearchInput] = useState("");
  const [contrepartieSelectionParTx, setContrepartieSelectionParTx] = useState<Record<string, CompteOption>>({});
  const [contrepartieCatalog, setContrepartieCatalog] = useState<ContrepartieCatalogOption[]>([]);
  const [bulkAffectModalOpen, setBulkAffectModalOpen] = useState(false);
  const [bulkContrepartieSearch, setBulkContrepartieSearch] = useState("");
  const [bulkContrepartiePick, setBulkContrepartiePick] = useState<CompteOption | null>(null);
  const [bulkAffectLoading, setBulkAffectLoading] = useState(false);
  const contrepartieDropdownRef = useRef<HTMLDivElement | null>(null);

  const PAGE_SIZE = 25;

  useEffect(() => {
    const onDocClick = (ev: MouseEvent) => {
      if (!contrepartieDropdownRef.current) return;
      const target = ev.target as Node | null;
      if (target && !contrepartieDropdownRef.current.contains(target)) {
        setContrepartieOpenTxId(null);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [activeDossier?.id]);

  useEffect(() => {
    if (!activeDossier?.id) {
      setContrepartieCatalog([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const rawBase = import.meta.env.BASE_URL || "/";
        const baseUrl = rawBase.endsWith("/") ? rawBase : `${rawBase}/`;
        const [jsonRes, fRes, cRes] = await Promise.all([
          fetch(`${baseUrl}syscohada_comptes_options.json`),
          supabase
            .from("fournisseurs")
            .select("nom, compte_auxiliaire, numero_compte")
            .eq("dossier_id", activeDossier.id)
            .or("compte_auxiliaire.not.is.null,numero_compte.not.is.null")
            .limit(500),
          supabase
            .from("clients")
            .select("nom, numero_compte")
            .eq("dossier_id", activeDossier.id)
            .not("numero_compte", "is", null)
            .limit(500),
        ]);
        if (cancelled) return;
        if (!jsonRes.ok) throw new Error(`SYSCOHADA JSON ${jsonRes.status}`);
        if (fRes.error) console.error("[COMPTA] fournisseurs contrepartie:", fRes.error);
        if (cRes.error) console.error("[COMPTA] clients contrepartie:", cRes.error);
        const jsonRows = (await jsonRes.json()) as SyscohadaCompteJsonRow[];
        const fromJson = buildContrepartieCatalogFromSyscohadaJson(Array.isArray(jsonRows) ? jsonRows : []);

        const fourOpts: ContrepartieCatalogOption[] = (fRes.data || []).flatMap(
          (r: {
            nom?: string | null;
            compte_auxiliaire?: string | null;
            numero_compte?: string | null;
          }) => {
            const aux = String(r.compte_auxiliaire ?? "").trim();
            const num = String(r.numero_compte ?? "").trim();
            const rawNum = aux || num;
            const libelle = String(r.nom ?? "").trim();
            const numero = formatCompte8(rawNum) || rawNum;
            if (!numero || !libelle) return [];
            const searchLower = `${rawNum.toLowerCase()} ${numero.toLowerCase()} ${libelle.toLowerCase()}`.trim();
            return [{ numero, libelle, group: "fournisseurs" as const, searchLower } satisfies ContrepartieCatalogOption];
          }
        );

        const cliOpts: ContrepartieCatalogOption[] = (cRes.data || []).flatMap(
          (r: { nom?: string | null; numero_compte?: string | null }) => {
            const rawNum = String(r.numero_compte ?? "").trim();
            const libelle = String(r.nom ?? "").trim();
            const numero = formatCompte8(rawNum) || rawNum;
            if (!numero || !libelle) return [];
            const searchLower = `${rawNum.toLowerCase()} ${numero.toLowerCase()} ${libelle.toLowerCase()}`.trim();
            return [{ numero, libelle, group: "clients" as const, searchLower } satisfies ContrepartieCatalogOption];
          }
        );

        const merged: ContrepartieCatalogOption[] = [...fromJson, ...fourOpts, ...cliOpts];
        const dedup = new Map<string, ContrepartieCatalogOption>();
        for (const o of merged) {
          const k = `${o.group}:${o.numero}`;
          if (!dedup.has(k)) dedup.set(k, o);
        }
        if (!cancelled) setContrepartieCatalog([...dedup.values()]);
      } catch (e) {
        console.error("[COMPTA] Chargement catalogue contrepartie:", e);
        if (!cancelled) setContrepartieCatalog([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeDossier?.id]);

  useEffect(() => {
    if (!activeDossier?.id) {
      setLoading(false);
      setTransactions([]);
      setTxIdsAchat(new Set());
      setTxHas471({});
      setTxHasEcritureComptable({});
      setTxContrepartieEcriture({});
      return;
    }
    setLoading(true);
    Promise.all([
      fetchTransactionsForCompta(activeDossier.id),
      fetchTransactionIdsWithFactureVenteRapprochee(),
    ])
      .then(([txs, vente]) => {
        setTransactions(txs || []);
        setTxIdsVente(vente);
      })
      .catch((e) => {
        console.error("TransactionsCompta fetch:", e);
        setTransactions([]);
      })
      .finally(() => setLoading(false));
  }, [activeDossier?.id]);

  const refresh = () => {
    if (!activeDossier?.id) return;
    fetchTransactionsForCompta(activeDossier.id).then(setTransactions).catch(() => {});
    fetchTransactionIdsWithFactureVenteRapprochee().then(setTxIdsVente);
  };

  const refreshTransactionsCommentaires = async (txIds: string[]) => {
    if (!activeDossier?.id || txIds.length === 0) {
      setTxIdsCommentaires(new Set());
      return;
    }
    try {
      const { data, error } = await supabase
        .from("commentaires")
        .select("entite_id")
        .eq("dossier_id", activeDossier.id)
        .eq("entite_type", "transaction")
        .is("deleted_at", null)
        .in("entite_id", txIds);
      if (error) throw error;
      setTxIdsCommentaires(
        new Set((data || []).map((r: { entite_id?: string | null }) => String(r.entite_id ?? "").trim()).filter(Boolean))
      );
    } catch (err) {
      console.error("[COMPTA] Batch commentaires transactions:", err);
      setTxIdsCommentaires(new Set());
    }
  };

  useEffect(() => {
    const txIds = transactions.map((t) => t.id).filter(Boolean);
    if (!txIds.length) {
      setTxIdsAchat(new Set());
      setTxHas471({});
      setTxHasEcritureComptable({});
      setTxContrepartieEcriture({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data: rapRows, error: rapErr } = await supabase
          .from("facture_achat_rapprochements")
          .select("transaction_id")
          .in("transaction_id", txIds);
        if (rapErr) throw rapErr;
        const achatSet = new Set((rapRows || []).map((r: { transaction_id: string }) => r.transaction_id));

        const { data: ecritures, error: ecErr } = await supabase
          .from("ecritures_comptables")
          .select("id, source_id, created_at")
          .eq("source_type", "transaction")
          .in("source_id", txIds)
          .order("created_at", { ascending: false });
        if (ecErr) throw ecErr;
        const ecritureByTx = new Map<string, string>();
        const ecritureIds: string[] = [];
        for (const ec of ecritures || []) {
          const sid = String((ec as { source_id?: string }).source_id ?? "");
          const id = String((ec as { id?: string }).id ?? "");
          if (!sid || !id || ecritureByTx.has(sid)) continue;
          ecritureByTx.set(sid, id);
          ecritureIds.push(id);
        }

        const has471ByTx: Record<string, boolean> = {};
        const hasEcritureByTx: Record<string, boolean> = {};
        const contrepartieByTx: Record<string, CompteOption | null> = {};
        txIds.forEach((id) => {
          has471ByTx[id] = false;
          hasEcritureByTx[id] = ecritureByTx.has(id);
          contrepartieByTx[id] = null;
        });

        if (ecritureIds.length) {
          const { data: allLignes, error: lErr } = await supabase
            .from("ecritures_comptables_lignes")
            .select("ecriture_id, compte_numero, compte_libelle, debit, credit")
            .in("ecriture_id", ecritureIds);
          if (lErr) throw lErr;
          const lignesByEcriture = new Map<string, LigneEcritureRow[]>();
          for (const row of allLignes || []) {
            const r = row as LigneEcritureRow;
            const eid = String(r.ecriture_id ?? "");
            if (!eid) continue;
            const arr = lignesByEcriture.get(eid) ?? [];
            arr.push(r);
            lignesByEcriture.set(eid, arr);
          }
          for (const [txId, ecId] of ecritureByTx.entries()) {
            const lignes = lignesByEcriture.get(ecId) ?? [];
            has471ByTx[txId] = lignes.some((l) => /^471/.test(String(l.compte_numero ?? "").trim()));
            contrepartieByTx[txId] = pickContrepartieDepuisLignesEcriture(lignes);
          }
        }

        if (!cancelled) {
          setTxIdsAchat(achatSet);
          setTxHas471(has471ByTx);
          setTxHasEcritureComptable(hasEcritureByTx);
          setTxContrepartieEcriture(contrepartieByTx);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("[COMPTA] Batch statut transactions:", err);
          setTxIdsAchat(new Set());
          setTxHas471({});
          setTxHasEcritureComptable({});
          setTxContrepartieEcriture({});
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [transactions]);

  useEffect(() => {
    const txIds = transactions.map((t) => t.id).filter(Boolean);
    void refreshTransactionsCommentaires(txIds);
  }, [transactions, activeDossier?.id]);

  const selectedTx = transactions.find((t) => t.id === selectedId);

  const filteredTransactions = useMemo(() => {
    let list = transactions;
    if (selectedSource !== "tous") {
      list = list.filter((t) => t.operateur === selectedSource);
    }
    if (statutFilter) {
      list = list.filter(
        (t) => statutAfficheTransaction(t, contrepartieSelectionParTx, txHas471, txIdsAchat, txIdsCommentaires) === statutFilter
      );
    }
    if (secondaryFilter === "avec_piece") {
      list = list.filter((t) => !!(t.piece_nom || t.piece_url));
    } else if (secondaryFilter === "sans_piece") {
      list = list.filter((t) => !t.piece_nom && !t.piece_url);
    }
    if (contrepartieSearch.trim()) {
      const q = contrepartieSearch.trim().toLowerCase();
      list = list.filter((t) => extractContrepartie(t.libelle).toLowerCase().includes(q) || (t.libelle ?? "").toLowerCase().includes(q));
    }
    if (montantExact.trim() && secondaryFilter === "montant" && montantMode === "exact") {
      const v = parseFloat(montantExact.replace(/\s/g, "").replace(",", "."));
      if (!isNaN(v)) list = list.filter((t) => Math.abs(Number(t.montant_ttc) || 0) >= v - 1 && Math.abs(Number(t.montant_ttc) || 0) <= v + 1);
    }
    if (montantMin.trim() && montantMax.trim() && secondaryFilter === "montant" && montantMode === "between") {
      const mn = parseFloat(montantMin.replace(/\s/g, "").replace(",", "."));
      const mx = parseFloat(montantMax.replace(/\s/g, "").replace(",", "."));
      if (!isNaN(mn) && !isNaN(mx)) list = list.filter((t) => {
        const amt = Math.abs(Number(t.montant_ttc) || 0);
        return amt >= Math.min(mn, mx) && amt <= Math.max(mn, mx);
      });
    }
    if (dateFrom && secondaryFilter === "date") {
      list = list.filter((t) => (t.date ?? "") >= dateFrom);
    }
    if (dateTo && secondaryFilter === "date") {
      list = list.filter((t) => (t.date ?? "") <= dateTo);
    }
    return list;
  }, [transactions, selectedSource, statutFilter, secondaryFilter, txIdsVente, txIdsAchat, txIdsCommentaires, txHas471, contrepartieSelectionParTx, contrepartieSearch, montantExact, montantMin, montantMax, montantMode, dateFrom, dateTo]);

  const totalCount = filteredTransactions.length;
  const statutTabCounts = useMemo(() => {
    const base = selectedSource === "tous" ? transactions : transactions.filter((t) => t.operateur === selectedSource);
    return {
      a_traiter: base.filter((t) => statutAfficheTransaction(t, contrepartieSelectionParTx, txHas471, txIdsAchat, txIdsCommentaires) === "a_traiter").length,
      pre_traitee: base.filter((t) => statutAfficheTransaction(t, contrepartieSelectionParTx, txHas471, txIdsAchat, txIdsCommentaires) === "pre_traitee").length,
      traitee: base.filter((t) => statutAfficheTransaction(t, contrepartieSelectionParTx, txHas471, txIdsAchat, txIdsCommentaires) === "traitee").length,
      demande_comptable: base.filter((t) => statutAfficheTransaction(t, contrepartieSelectionParTx, txHas471, txIdsAchat, txIdsCommentaires) === "demande_comptable").length,
    };
  }, [transactions, selectedSource, txIdsVente, txIdsAchat, txIdsCommentaires, txHas471, contrepartieSelectionParTx]);
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const paginatedTransactions = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filteredTransactions.slice(start, start + PAGE_SIZE);
  }, [filteredTransactions, safePage]);
  const paginatedIds = useMemo(() => paginatedTransactions.map((t) => t.id), [paginatedTransactions]);
  const allPageSelected =
    paginatedIds.length > 0 && paginatedIds.every((id) => selectedIds.has(id));
  const startIdx = totalCount === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const endIdx = Math.min(safePage * PAGE_SIZE, totalCount);

  const handleRetirerJustificatifAttenduGroupe = async () => {
    if (selectedIds.size < 2) return;
    setBulkJustifLoading(true);
    const ids = [...selectedIds];
    const patches = new Map<string, { statut_justification?: "justifiee" | "non_justifiee" }>();
    let ok = 0;
    let fail = 0;
    try {
      for (const id of ids) {
        try {
          const meta = await updateTransactionJustificatifCompta(id, {
            justificatif_requis: false,
            justificatif_statut: "non_requis",
          });
          patches.set(id, meta);
          ok += 1;
        } catch {
          fail += 1;
        }
      }
      if (patches.size > 0) {
        setTransactions((prev) =>
          prev.map((t) => {
            const meta = patches.get(t.id);
            if (!meta) return t;
            return {
              ...t,
              justificatif_requis: false,
              justificatif_statut: "non_requis",
              ...(meta.statut_justification !== undefined ? { statut_justification: meta.statut_justification } : {}),
            };
          })
        );
      }
      setSelectedIds(new Set());
      if (ok > 0) {
        const base =
          ok === 1 ? "1 transaction mise à jour" : `${ok} transactions mises à jour`;
        const msg = fail > 0 ? `${base} (${fail} échec${fail > 1 ? "s" : ""})` : base;
        setComptaBulkToastType("success");
        setComptaBulkToast(msg);
        window.setTimeout(() => setComptaBulkToast(null), 4500);
      } else if (fail > 0) {
        setComptaBulkToastType("error");
        setComptaBulkToast("Aucune transaction mise à jour.");
        window.setTimeout(() => setComptaBulkToast(null), 4500);
      }
    } finally {
      setBulkJustifLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
  }, [selectedSource, statutFilter, secondaryFilter, contrepartieSearch, montantExact, montantMin, montantMax, dateFrom, dateTo]);

  const balanceForSource = useMemo(() => {
    if (selectedSource === "tous") {
      return transactions.reduce((s, t) => s + (t.type === "entree" ? 1 : -1) * Math.abs(Number(t.montant_ttc) || 0), 0);
    }
    return transactions
      .filter((t) => t.operateur === selectedSource)
      .reduce((s, t) => s + (t.type === "entree" ? 1 : -1) * Math.abs(Number(t.montant_ttc) || 0), 0);
  }, [transactions, selectedSource]);

  const contrepartieCatalogAvecHistorique = useMemo(() => {
    const byKey = new Map<string, ContrepartieCatalogOption>();
    for (const o of contrepartieCatalog) byKey.set(`${o.group}:${o.numero}`, o);
    for (const tx of transactions) {
      for (const l of tx.transaction_lignes || []) {
        const raw = String(l.compte_numero ?? "").trim();
        const n8 = formatCompte8(raw) || raw;
        if (!n8 || /^471/.test(n8) || isCompteTresoreriePrincipal(n8)) continue;
        const libelle = String(l.compte_libelle ?? "").trim();
        if (!libelle) continue;
        const group: ContrepartieSourceGroup = n8.startsWith("6")
          ? "charges"
          : n8.startsWith("7")
            ? "produits"
            : "autres";
        const k = `${group}:${n8}`;
        if (byKey.has(k)) continue;
        byKey.set(k, {
          numero: n8,
          libelle,
          group,
          searchLower: `${raw.toLowerCase()} ${n8.toLowerCase()} ${libelle.toLowerCase()}`.trim(),
        });
      }
    }
    return [...byKey.values()];
  }, [contrepartieCatalog, transactions]);

  const contrepartieDropdownFiltered = useMemo(
    () => filterContrepartieCatalog(contrepartieCatalogAvecHistorique, contrepartieSearchInput, MAX_CONTREPARTIE_DROPDOWN),
    [contrepartieCatalogAvecHistorique, contrepartieSearchInput]
  );
  const contrepartieDropdownRows = useMemo(
    () => groupedContrepartieRows(contrepartieDropdownFiltered),
    [contrepartieDropdownFiltered]
  );

  const bulkContrepartieFiltered = useMemo(
    () => filterContrepartieCatalog(contrepartieCatalogAvecHistorique, bulkContrepartieSearch, MAX_CONTREPARTIE_DROPDOWN),
    [contrepartieCatalogAvecHistorique, bulkContrepartieSearch]
  );
  const bulkContrepartieRows = useMemo(() => groupedContrepartieRows(bulkContrepartieFiltered), [bulkContrepartieFiltered]);


  const appliquerContrepartie = async (txId: string, c: CompteOption) => {
    const prevSelection = contrepartieSelectionParTx[txId];
    const prevTx = transactions.find((t) => t.id === txId);
    const prevHas471 = txHas471[txId] === true;
    const prevContrepartieEc = txContrepartieEcriture[txId] ?? null;
    const prevHasEcriture = txHasEcritureComptable[txId] === true;

    setContrepartieSelectionParTx((prev) => ({ ...prev, [txId]: c }));
    setTxHas471((prev) => ({ ...prev, [txId]: false }));
    setTxHasEcritureComptable((prev) => ({ ...prev, [txId]: true }));
    setTxContrepartieEcriture((prev) => ({ ...prev, [txId]: c }));
    setTransactions((prev) =>
      prev.map((t) =>
        t.id === txId
          ? {
              ...t,
              statut_justification: "justifiee",
            }
          : t
      )
    );
    setContrepartieOpenTxId(null);
    setContrepartieSearchInput("");

    try {
      await persistContrepartieTransaction(txId, c);
      setComptaBulkToastType("success");
      setComptaBulkToast("Écriture générée ✓");
      window.setTimeout(() => setComptaBulkToast(null), 2500);
    } catch (err) {
      setContrepartieSelectionParTx((prev) => {
        const next = { ...prev };
        if (prevSelection) next[txId] = prevSelection;
        else delete next[txId];
        return next;
      });
      if (prevTx) {
        setTransactions((prev) =>
          prev.map((t) =>
            t.id === txId
              ? {
                  ...t,
                  statut_justification: prevTx.statut_justification,
                }
              : t
          )
        );
      }
      setTxHas471((prev) => ({ ...prev, [txId]: prevHas471 }));
      setTxHasEcritureComptable((prev) => ({ ...prev, [txId]: prevHasEcriture }));
      setTxContrepartieEcriture((prev) => ({ ...prev, [txId]: prevContrepartieEc }));
      setComptaBulkToastType("error");
      const msg =
        err instanceof Error && err.message
          ? err.message
          : "Erreur lors de la mise à jour de la contrepartie";
      setComptaBulkToast(msg);
      window.setTimeout(() => setComptaBulkToast(null), 3500);
      console.error("[COMPTA] Mise à jour contrepartie:", err);
    }
  };

  const bulkPreviewTransactions = useMemo(
    () =>
      [...transactions].filter((t) => selectedIds.has(t.id)).sort((a, b) => (b.date || "").localeCompare(a.date || "")),
    [transactions, selectedIds]
  );

  const handleConfirmerAffectationMasse = async () => {
    if (!activeDossier?.id || !bulkContrepartiePick || selectedIds.size < 2) return;
    const c = bulkContrepartiePick;
    const ids = [...selectedIds];
    setBulkAffectLoading(true);
    let ok = 0;
    let fail = 0;
    for (const id of ids) {
      try {
        await persistContrepartieTransaction(id, c);
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    setBulkAffectLoading(false);
    setBulkAffectModalOpen(false);
    setBulkContrepartiePick(null);
    setBulkContrepartieSearch("");
    setSelectedIds(new Set());
    try {
      const txs = await fetchTransactionsForCompta(activeDossier.id);
      setTransactions(txs || []);
    } catch {
      refresh();
    }
    setComptaBulkToastType(ok > 0 && fail === 0 ? "success" : fail > 0 && ok === 0 ? "error" : "success");
    const msg =
      fail === 0 && ok > 0
        ? ok === 1
          ? "1 transaction affectée ✓"
          : `${ok} transactions affectées ✓`
        : ok === 0
          ? "Aucune transaction affectée."
          : `${ok} transaction${ok > 1 ? "s" : ""} affectée${ok > 1 ? "s" : ""} (${fail} échec${fail > 1 ? "s" : ""})`;
    setComptaBulkToast(msg);
    window.setTimeout(() => setComptaBulkToast(null), fail > 0 ? 5500 : 4000);
  };

  if (!activeDossier) {
    return (
      <div style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif", padding: 24 }}>
        <div style={{ fontWeight: 800, marginBottom: 8, color: "#1A0A00" }}>Sélectionnez un dossier</div>
        <div style={{ fontSize: "0.9rem", color: "#9A8878" }}>Choisissez un dossier pour consulter les transactions.</div>
      </div>
    );
  }

  return (
    <div
      style={{
        fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 64px - 56px)",
        background: "#f8fafc",
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "20px 24px", borderBottom: "1px solid #e2e8f0", background: "white" }}>
        {/* Source dropdown */}
        <div style={{ marginBottom: 20, position: "relative" }}>
          <button
            onClick={() => setSourceOpen(!sourceOpen)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "12px 16px",
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: 12,
              cursor: "pointer",
              fontSize: 14,
              fontWeight: 600,
              color: "#0f172a",
            }}
          >
            {(() => {
              const op = OPERATEURS.find((o) => o.id === selectedSource);
              return op?.logo ? (
                <img src={op.logo} alt="" style={{ width: 24, height: 24, borderRadius: 6, objectFit: "contain" }} />
              ) : (
                <div style={{ width: 24, height: 24, borderRadius: 6, background: "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "#64748b" }}>
                  {op?.icon ?? "T"}
                </div>
              );
            })()}
            <span>{OPERATEURS.find((o) => o.id === selectedSource)?.label ?? "Source"}</span>
            <span style={{ marginLeft: "auto", color: balanceForSource >= 0 ? "#16a34a" : "#dc2626", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
              {balanceForSource >= 0 ? "+ " : ""}{fmt(balanceForSource)}
            </span>
            <ChevronDown size={18} style={{ opacity: 0.6, transform: sourceOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
          </button>
          {sourceOpen && (
            <>
              <div style={{ position: "fixed", inset: 0, zIndex: 40 }} onClick={() => setSourceOpen(false)} />
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  marginTop: 6,
                  background: "white",
                  border: "1px solid #e2e8f0",
                  borderRadius: 12,
                  boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
                  padding: 6,
                  minWidth: 280,
                  zIndex: 50,
                }}
              >
                {OPERATEURS.map((op) => (
                  <button
                    key={op.id}
                    onClick={() => { setSelectedSource(op.id); setSourceOpen(false); }}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "10px 14px",
                      border: "none",
                      background: selectedSource === op.id ? "#fff7ed" : "transparent",
                      borderRadius: 8,
                      cursor: "pointer",
                      fontSize: 14,
                      textAlign: "left",
                    }}
                  >
                    {op.logo ? (
                      <img src={op.logo} alt="" style={{ width: 24, height: 24, borderRadius: 6, objectFit: "contain" }} />
                    ) : (
                      <div style={{ width: 24, height: 24, borderRadius: 6, background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "#64748b" }}>
                        {op.icon ?? "?"}
                      </div>
                    )}
                    {op.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Filtres ligne 1 */}
        <div style={{ display: "flex", borderBottom: "1px solid #e5e7eb", marginBottom: 12 }}>
          {STATUT_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setStatutFilter(statutFilter === f.id ? null : f.id)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 16px",
                marginBottom: -1,
                border: "none",
                borderBottom:
                  statutFilter === f.id
                    ? f.id === "pre_traitee"
                      ? "2px solid #3b82f6"
                      : "2px solid #f97316"
                    : "2px solid transparent",
                background: "transparent",
                color:
                  statutFilter === f.id
                    ? f.id === "pre_traitee"
                      ? "#3b82f6"
                      : "#f97316"
                    : "#6b7280",
                fontSize: 14,
                fontWeight: statutFilter === f.id ? 600 : 500,
                cursor: "pointer",
                transition: "color 0.15s, border-color 0.15s",
              }}
            >
              {f.label}
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 11,
                  fontWeight: 700,
                  padding: "2px 6px",
                  borderRadius: 999,
                  background:
                    statutFilter === f.id
                      ? f.id === "pre_traitee"
                        ? "#eff6ff"
                        : "#fff7ed"
                      : "#f3f4f6",
                  color:
                    statutFilter === f.id
                      ? f.id === "pre_traitee"
                        ? "#2563eb"
                        : "#ea580c"
                      : "#6b7280",
                }}
              >
                {statutTabCounts[f.id]}
              </span>
            </button>
          ))}
        </div>

        {/* Filtres ligne 2 */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
          {SECONDARY_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setSecondaryFilter(secondaryFilter === f.id ? null : f.id)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "4px 10px",
                borderRadius: 6,
                border: "1px solid #fed7aa",
                background: "white",
                color: "#f97316",
                fontSize: 12,
                fontWeight: 500,
                cursor: "pointer",
                transition: "color 0.15s, border-color 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#fb923c";
                e.currentTarget.style.color = "#f97316";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#fed7aa";
                e.currentTarget.style.color = "#f97316";
              }}
            >
              <f.icon size={14} strokeWidth={2} color="#fb923c" />
              {f.label}
            </button>
          ))}
          {secondaryFilter === "montant" && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: 8 }}>
              <select
                value={montantMode}
                onChange={(e) => setMontantMode(e.target.value as "exact" | "between")}
                style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }}
              >
                <option value="exact">Montant exact</option>
                <option value="between">Entre ... et ...</option>
              </select>
              {montantMode === "exact" ? (
                <input
                  type="text"
                  placeholder="Montant (FCFA)"
                  value={montantExact}
                  onChange={(e) => setMontantExact(e.target.value)}
                  style={{ width: 140, padding: "6px 12px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }}
                />
              ) : (
                <>
                  <input
                    type="text"
                    placeholder="Min"
                    value={montantMin}
                    onChange={(e) => setMontantMin(e.target.value)}
                    style={{ width: 100, padding: "6px 12px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }}
                  />
                  <span style={{ color: "#94a3b8" }}>–</span>
                  <input
                    type="text"
                    placeholder="Max"
                    value={montantMax}
                    onChange={(e) => setMontantMax(e.target.value)}
                    style={{ width: 100, padding: "6px 12px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }}
                  />
                </>
              )}
            </div>
          )}
          {secondaryFilter === "contrepartie" && (
            <input
              type="text"
              placeholder="Rechercher contrepartie..."
              value={contrepartieSearch}
              onChange={(e) => setContrepartieSearch(e.target.value)}
              style={{ width: 200, padding: "6px 12px", marginLeft: 8, borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }}
            />
          )}
          {secondaryFilter === "date" && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: 8 }}>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }}
              />
              <span style={{ color: "#94a3b8" }}>→</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }}
              />
            </div>
          )}
        </div>
      </div>

      <div style={{ flex: 1, overflow: "auto", paddingBottom: selectedIds.size >= 2 ? 88 : 0 }}>
        {loading ? (
          <div style={{ padding: 48, textAlign: "center", color: "#475569" }}>
            <Loader2 size={32} className="animate-spin" style={{ animation: "spin 1s linear infinite" }} />
          </div>
        ) : (
          <table style={{ width: "100%", minWidth: 1280, borderCollapse: "collapse", background: "white", tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: 16 }} />
              <col style={{ width: 124 }} />
              <col style={{ width: 64 }} />
              <col style={{ width: 32 }} />
              <col style={{ width: 180 }} />
              <col style={{ width: 128 }} />
              <col style={{ width: 300 }} />
              <col style={{ width: 120 }} />
              <col style={{ width: 80 }} />
              <col style={{ width: 120 }} />
            </colgroup>
            <thead>
              <tr style={{ borderBottom: "1px solid #f3f4f6" }}>
                <th style={{ padding: "6px 4px", textAlign: "center", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", width: 16 }}>
                  <input
                    type="checkbox"
                    checked={allPageSelected}
                    disabled={paginatedIds.length === 0}
                    title="Sélectionner toutes les transactions de cette page"
                    onChange={() => {
                      setSelectedIds((prev) => {
                        const next = new Set(prev);
                        if (allPageSelected) paginatedIds.forEach((id) => next.delete(id));
                        else paginatedIds.forEach((id) => next.add(id));
                        return next;
                      });
                    }}
                    style={{
                      width: 14,
                      height: 14,
                      accentColor: "#FF9500",
                      cursor: paginatedIds.length === 0 ? "not-allowed" : "pointer",
                      verticalAlign: "middle",
                    }}
                  />
                </th>
                <th style={{ padding: "6px 6px", textAlign: "left", fontSize: 11, fontWeight: 500, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em", width: 124 }}>STATUT</th>
                <th style={{ padding: "6px 6px", textAlign: "left", fontSize: 11, fontWeight: 500, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em", width: 64 }}>DATE</th>
                <th style={{ padding: "6px 6px", textAlign: "center", fontSize: 11, fontWeight: 500, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em", width: 32 }}>JNL</th>
                <th style={{ padding: "6px 4px", textAlign: "left", fontSize: 11, fontWeight: 500, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em", width: 180, maxWidth: 180 }}>LIBELLÉ</th>
                <th style={{ padding: "6px 4px", textAlign: "right", fontSize: 11, fontWeight: 500, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em", width: 128 }}>MONTANT</th>
                <th style={{ padding: "6px 12px", textAlign: "left", fontSize: 11, fontWeight: 500, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em", width: 300 }}>CONTREPARTIE</th>
                <th style={{ padding: "6px 12px", textAlign: "center", fontSize: 11, fontWeight: 500, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em", width: 120 }}>SOURCE</th>
                <th style={{ padding: "6px 12px", textAlign: "center", fontSize: 11, fontWeight: 500, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em", width: 80 }}>JUSTIF.</th>
                <th style={{ padding: "6px 12px", textAlign: "center", fontSize: 11, fontWeight: 500, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em", width: 120 }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ padding: 48, textAlign: "center", color: "#475569", fontSize: 14 }}>
                    {transactions.length === 0 ? "Aucune transaction. Créez des transactions depuis Gestion → Transactions." : "Aucune transaction ne correspond aux filtres."}
                  </td>
                </tr>
              ) : paginatedTransactions.map((tx) => {
                const isEntree = tx.type === "entree";
                const isSelected = selectedId === tx.id;
                const isMultiSelected = selectedIds.has(tx.id);
                const modeTx = (tx as { mode_traitement?: string | null }).mode_traitement;
                const sourceRaw = (tx as { source?: string | null; source_type?: string | null; is_import?: boolean | null });
                const justifTx = normalizeJustificatifStatut(
                  (tx as { justificatif_statut?: string | null }).justificatif_statut ?? undefined
                );
                const justifAttenduCompta =
                  modeTx === "tresorerie_directe" && justifTx === "en_attente";
                const statutCourant = statutAfficheTransaction(tx, contrepartieSelectionParTx, txHas471, txIdsAchat, txIdsCommentaires);
                const statutLigne = statutCourant === "traitee"
                  ? { label: "Traitée", bg: "#d1fae5", color: "#047857", border: "#a7f3d0" }
                  : statutCourant === "pre_traitee"
                    ? { label: "Pré-traitée", bg: "#dbeafe", color: "#1d4ed8", border: "#bfdbfe" }
                  : statutCourant === "demande_comptable"
                    ? { label: "Demande comptable", bg: "#dbeafe", color: "#1d4ed8", border: "#bfdbfe" }
                    : { label: "À traiter", bg: "#fef3c7", color: "#b45309", border: "#fde68a" };
                const journalBadge = journalBadgeForTx(tx);
                const contrepartieHistorique = (() => {
                  const ligne = (tx.transaction_lignes || [])[0];
                  if (!ligne?.compte_numero) return null;
                  return {
                    numero: String(ligne.compte_numero),
                    libelle: String(ligne.compte_libelle || "").trim(),
                  } as CompteOption;
                })();
                const fc = tx.facture_achat_charge_compte;
                const contrepartieDepuisFactureAchat =
                  fc && fc.numero
                    ? ({ numero: String(fc.numero), libelle: String(fc.libelle || "").trim() || "Charge" } as CompteOption)
                    : null;
                const contrepartieCourante =
                  contrepartieSelectionParTx[tx.id] ??
                  contrepartieDepuisFactureAchat ??
                  contrepartieHistorique;
                const contrepartieVerrouillee =
                  tx.statut_justification === "justifiee" ||
                  (txHasEcritureComptable[tx.id] === true && txHas471[tx.id] !== true);
                const fromEcCp = txContrepartieEcriture[tx.id];
                const texteContrepartieAffiche =
                  fromEcCp && String(fromEcCp.numero || "").trim()
                    ? afficheNumeroContrepartie(fromEcCp)
                    : contrepartieCourante && String(contrepartieCourante.numero || "").trim()
                      ? afficheNumeroContrepartie(contrepartieCourante)
                      : "—";
                const isImport =
                  sourceRaw.is_import === true ||
                  String(sourceRaw.source ?? sourceRaw.source_type ?? "").toLowerCase().includes("import") ||
                  tx.operateur !== "manuel";
                const sourceBadge = isImport
                  ? { label: "Import", bg: "#dbeafe", color: "#2563eb" }
                  : { label: "Saisie", bg: "#f3f4f6", color: "#4b5563" };
                const rowBg = isSelected
                  ? "linear-gradient(90deg, rgba(255,149,0,0.08), rgba(255,149,0,0.04))"
                  : isMultiSelected
                    ? "rgba(255,149,0,0.06)"
                    : "transparent";
                return (
                  <tr
                    key={tx.id}
                    onClick={() => setSelectedId(tx.id)}
                    style={{
                      cursor: "pointer",
                      background: rowBg,
                      borderBottom: "1px solid #f3f4f6",
                      borderLeft: isSelected ? "4px solid #FF9500" : isMultiSelected ? "4px solid rgba(255,149,0,0.45)" : "4px solid transparent",
                      transition: "background-color 0.15s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected && !isMultiSelected) {
                        e.currentTarget.style.background = "#f9fafb";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected && !isMultiSelected) {
                        e.currentTarget.style.background = rowBg;
                      }
                    }}
                  >
                    <td
                      style={{ padding: "6px 4px", textAlign: "center", width: 16, verticalAlign: "middle" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.has(tx.id)}
                        title="Sélectionner pour action groupée"
                        onChange={() => {
                          setSelectedIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(tx.id)) next.delete(tx.id);
                            else next.add(tx.id);
                            return next;
                          });
                        }}
                        style={{ width: 14, height: 14, accentColor: "#FF9500", cursor: "pointer", verticalAlign: "middle" }}
                      />
                    </td>
                    <td style={{ padding: "6px 4px", textAlign: "left", width: 124 }}>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          fontSize: 11,
                          fontWeight: 600,
                          padding: "2px 8px",
                          borderRadius: 999,
                          background: statutLigne.bg,
                          color: statutLigne.color,
                          border: `1px solid ${statutLigne.border}`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {statutLigne.label}
                      </span>
                    </td>
                    <td style={{ padding: "6px 4px", fontSize: 11, color: "#6b7280", whiteSpace: "nowrap", width: 64 }}>
                      {fmtDateCourt(tx.date ?? "")}
                    </td>
                    <td style={{ padding: "6px 4px", textAlign: "center", width: 32 }}>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: 28,
                          height: 18,
                          borderRadius: 999,
                          border: "none",
                          background:
                            journalBadge === "BQ"
                              ? "#ede9fe"
                              : journalBadge === "CA"
                                ? "#e0f2fe"
                                : "#f3f4f6",
                          fontSize: 10,
                          fontWeight: 700,
                          color:
                            journalBadge === "BQ"
                              ? "#6d28d9"
                              : journalBadge === "CA"
                                ? "#0369a1"
                                : "#4b5563",
                        }}
                      >
                        {journalBadge}
                      </span>
                    </td>
                    <td style={{ padding: "6px 4px", fontSize: 14, fontWeight: 500, color: "#111827", width: 180, maxWidth: 180 }}>
                      <span style={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 180 }}>
                        {tx.libelle ?? "—"}
                      </span>
                    </td>
                    <td style={{ padding: "6px 4px", textAlign: "right", fontSize: 14, fontWeight: 500, color: isEntree ? "#16a34a" : "#dc2626", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", width: 128 }}>
                      {isEntree ? "+ " : "−"}{fmt(Math.abs(Number(tx.montant_ttc) || 0))}
                    </td>
                    <td style={{ padding: "6px 12px", position: "relative", width: 300 }} onClick={(e) => e.stopPropagation()}>
                      {contrepartieVerrouillee ? (
                        <span
                          className="text-sm text-slate-700"
                          style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        >
                          {texteContrepartieAffiche}
                        </span>
                      ) : (
                        <div ref={contrepartieOpenTxId === tx.id ? contrepartieDropdownRef : null}>
                          <button
                            type="button"
                            onClick={() => {
                              setContrepartieOpenTxId((prev) => (prev === tx.id ? null : tx.id));
                              setContrepartieSearchInput("");
                            }}
                            style={{
                              width: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 6,
                              padding: "4px 8px",
                              borderRadius: 8,
                              border: "1px solid #e5e7eb",
                              background: "#fff",
                              color: "#374151",
                              fontSize: 13,
                              cursor: "pointer",
                            }}
                          >
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "left" }}>
                              {contrepartieCourante ? afficheNumeroContrepartie(contrepartieCourante) : "Choisir..."}
                            </span>
                            <ChevronDown size={14} />
                          </button>
                          {contrepartieOpenTxId === tx.id ? (
                            <div
                              style={{
                                position: "absolute",
                                top: "calc(100% + 4px)",
                                left: 8,
                                right: 8,
                                zIndex: 60,
                                border: "1px solid #e5e7eb",
                                borderRadius: 10,
                                background: "#fff",
                                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
                                padding: 8,
                              }}
                            >
                              <input
                                value={contrepartieSearchInput}
                                onChange={(e) => setContrepartieSearchInput(e.target.value)}
                                placeholder="Numéro ou libellé (max. 20 résultats)…"
                                style={{
                                  width: "100%",
                                  padding: "6px 8px",
                                  border: "1px solid #e5e7eb",
                                  borderRadius: 8,
                                  fontSize: 13,
                                  marginBottom: 6,
                                }}
                              />
                              <div style={{ maxHeight: 220, overflowY: "auto", display: "flex", flexDirection: "column", gap: 0 }}>
                                {contrepartieDropdownRows.length === 0 ? (
                                  <div style={{ fontSize: 12, color: "#9ca3af", padding: "6px 4px" }}>Aucun compte.</div>
                                ) : (
                                  contrepartieDropdownRows.map((row, idx) =>
                                    row.kind === "header" ? (
                                      <div
                                        key={`hdr-${tx.id}-${row.label}-${idx}`}
                                        style={{
                                          fontSize: 10,
                                          fontWeight: 700,
                                          color: "#94a3b8",
                                          textTransform: "uppercase",
                                          letterSpacing: "0.04em",
                                          padding: "6px 6px 2px",
                                          marginTop: idx > 0 ? 4 : 0,
                                        }}
                                      >
                                        {row.label}
                                      </div>
                                    ) : (
                                      <button
                                        key={`${tx.id}-${row.opt.group}-${row.opt.numero}-${idx}`}
                                        type="button"
                                        onClick={() => appliquerContrepartie(tx.id, row.opt)}
                                        style={{
                                          border: "none",
                                          background: "transparent",
                                          textAlign: "left",
                                          padding: "5px 6px",
                                          borderRadius: 6,
                                          fontSize: 12,
                                          color: "#111827",
                                          cursor: "pointer",
                                        }}
                                      >
                                        <span style={{ fontWeight: 600 }}>{row.opt.numero}</span> — {row.opt.libelle || "Compte"}
                                      </button>
                                    )
                                  )
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  setContrepartieOpenTxId(null);
                                  setComptaBulkToastType("success");
                                  setComptaBulkToast("Astuce: créez la règle depuis Paramètres → Règles.");
                                  window.setTimeout(() => setComptaBulkToast(null), 3500);
                                }}
                                style={{
                                  marginTop: 6,
                                  width: "100%",
                                  border: "none",
                                  borderTop: "1px solid #f3f4f6",
                                  paddingTop: 6,
                                  textAlign: "left",
                                  background: "transparent",
                                  color: "#ea580c",
                                  fontSize: 12,
                                  fontWeight: 600,
                                  cursor: "pointer",
                                }}
                              >
                                + Créer une règle
                              </button>
                            </div>
                          ) : null}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "6px 12px", textAlign: "center", width: 120 }}>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 11,
                          fontWeight: 600,
                          padding: "2px 8px",
                          borderRadius: 999,
                          background: sourceBadge.bg,
                          color: sourceBadge.color,
                        }}
                      >
                        {sourceBadge.label}
                      </span>
                    </td>
                    <td style={{ padding: "6px 12px", textAlign: "center", width: 80 }}>
                      {(tx.piece_nom || tx.piece_url || txIdsVente.has(tx.id) || txIdsAchat.has(tx.id)) ? (
                        <span
                          title={tx.piece_nom ?? ((txIdsVente.has(tx.id) || txIdsAchat.has(tx.id)) ? "Rapprochée avec une facture" : "Pièce jointe")}
                          style={{ display: "inline-flex", alignItems: "center", color: "#16a34a" }}
                        >
                          <Paperclip size={14} strokeWidth={2} />
                        </span>
                      ) : (
                        <span style={{ color: "#d1d5db", fontSize: 12 }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: "6px 12px", textAlign: "center", width: 120 }} onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <button type="button" title="Attacher une pièce" className="tx-action-btn attach" style={{ border: "none", background: "transparent", color: "#9ca3af", cursor: "pointer", padding: 0, lineHeight: 0 }}>
                          <Paperclip size={14} />
                        </button>
                        <button type="button" title="Modifier la transaction" className="tx-action-btn edit" style={{ border: "none", background: "transparent", color: "#9ca3af", cursor: "pointer", padding: 0, lineHeight: 0 }}>
                          <Pencil size={14} />
                        </button>
                        <button type="button" title="Supprimer" className="tx-action-btn delete" style={{ border: "none", background: "transparent", color: "#9ca3af", cursor: "pointer", padding: 0, lineHeight: 0 }}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {selectedIds.size >= 2 && (
        <div
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 90,
            padding: "12px 24px",
            background: "white",
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
            boxShadow: "0 -8px 24px rgba(15, 23, 42, 0.08)",
            fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
          }}
        >
          <span style={{ fontSize: 14, color: "#475569", fontWeight: 600 }}>
            {selectedIds.size} transaction{selectedIds.size > 1 ? "s" : ""} sélectionnée{selectedIds.size > 1 ? "s" : ""}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => {
                setBulkContrepartieSearch("");
                setBulkContrepartiePick(null);
                setBulkAffectModalOpen(true);
              }}
              style={{
                padding: "8px 16px",
                borderRadius: 8,
                border: "none",
                background: "#f97316",
                color: "white",
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              Affecter en masse
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              style={{
                padding: "8px 14px",
                borderRadius: 8,
                border: "1px solid #e2e8f0",
                background: "white",
                color: "#64748b",
                fontWeight: 600,
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              Désélectionner
            </button>
            <button
              type="button"
              disabled={bulkJustifLoading}
              onClick={() => void handleRetirerJustificatifAttenduGroupe()}
              style={{
                padding: "8px 14px",
                borderRadius: 8,
                border: "1px solid #e2e8f0",
                background: "#fff",
                color: "#0f172a",
                fontWeight: 600,
                fontSize: 13,
                cursor: bulkJustifLoading ? "wait" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                opacity: bulkJustifLoading ? 0.75 : 1,
              }}
            >
              {bulkJustifLoading ? <Loader2 size={16} className="animate-spin" style={{ animation: "spin 1s linear infinite" }} /> : null}
              Retirer justificatif attendu
            </button>
          </div>
        </div>
      )}

      {bulkAffectModalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="bulk-affect-title"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 120,
            background: "rgba(15, 23, 42, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          onClick={() => !bulkAffectLoading && setBulkAffectModalOpen(false)}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 520,
              maxHeight: "90vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              background: "white",
              borderRadius: 12,
              boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
              fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: "16px 20px", borderBottom: "1px solid #e5e7eb" }}>
              <h2 id="bulk-affect-title" style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
                Affectation en masse
              </h2>
              <p style={{ margin: "8px 0 0", fontSize: 13, color: "#64748b" }}>
                Compte de contrepartie pour {selectedIds.size} transaction{selectedIds.size > 1 ? "s" : ""}.
              </p>
            </div>
            <div style={{ padding: "12px 20px", borderBottom: "1px solid #f3f4f6", flexShrink: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", marginBottom: 6 }}>COMPTE</div>
              <input
                value={bulkContrepartieSearch}
                onChange={(e) => setBulkContrepartieSearch(e.target.value)}
                placeholder="Rechercher numéro ou libellé…"
                disabled={bulkAffectLoading}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                  fontSize: 13,
                  marginBottom: 8,
                }}
              />
              <div style={{ maxHeight: 180, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 8, padding: 4 }}>
                {bulkContrepartieRows.length === 0 ? (
                  <div style={{ fontSize: 12, color: "#9ca3af", padding: 8 }}>Aucun compte.</div>
                ) : (
                  bulkContrepartieRows.map((row, idx) =>
                    row.kind === "header" ? (
                      <div
                        key={`b-h-${row.label}-${idx}`}
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: "#94a3b8",
                          textTransform: "uppercase",
                          padding: "6px 6px 2px",
                          marginTop: idx > 0 ? 4 : 0,
                        }}
                      >
                        {row.label}
                      </div>
                    ) : (
                      <button
                        key={`b-${row.opt.group}-${row.opt.numero}-${idx}`}
                        type="button"
                        disabled={bulkAffectLoading}
                        onClick={() => setBulkContrepartiePick(row.opt)}
                        style={{
                          display: "block",
                          width: "100%",
                          border: "none",
                          textAlign: "left",
                          padding: "6px 8px",
                          borderRadius: 6,
                          fontSize: 12,
                          background: bulkContrepartiePick?.numero === row.opt.numero ? "#fff7ed" : "transparent",
                          cursor: bulkAffectLoading ? "wait" : "pointer",
                          color: "#111827",
                        }}
                      >
                        <span style={{ fontWeight: 600 }}>{row.opt.numero}</span> — {row.opt.libelle || "Compte"}
                      </button>
                    )
                  )
                )}
              </div>
              {bulkContrepartiePick ? (
                <div style={{ marginTop: 8, fontSize: 12, color: "#0f172a" }}>
                  Sélection : <strong>{afficheNumeroContrepartie(bulkContrepartiePick)}</strong>
                </div>
              ) : (
                <div style={{ marginTop: 8, fontSize: 12, color: "#9ca3af" }}>Choisissez un compte dans la liste.</div>
              )}
            </div>
            <div style={{ padding: "12px 20px", flex: 1, minHeight: 0, overflow: "auto" }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", marginBottom: 8 }}>APERÇU</div>
              <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                {bulkPreviewTransactions.map((t) => {
                  const ent = t.type === "entree";
                  return (
                    <li
                      key={t.id}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 12,
                        padding: "6px 0",
                        borderBottom: "1px solid #f3f4f6",
                        fontSize: 12,
                        color: "#334155",
                      }}
                    >
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                        {fmtDateCourt(t.date ?? "")} — {t.libelle ?? "—"}
                      </span>
                      <span style={{ fontWeight: 600, color: ent ? "#16a34a" : "#dc2626", whiteSpace: "nowrap" }}>
                        {ent ? "+" : "−"}
                        {fmt(Math.abs(Number(t.montant_ttc) || 0))}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div style={{ padding: "14px 20px", borderTop: "1px solid #e5e7eb", display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                type="button"
                disabled={bulkAffectLoading}
                onClick={() => setBulkAffectModalOpen(false)}
                style={{
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                  background: "white",
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: bulkAffectLoading ? "wait" : "pointer",
                }}
              >
                Annuler
              </button>
              <button
                type="button"
                disabled={bulkAffectLoading || !bulkContrepartiePick}
                onClick={() => void handleConfirmerAffectationMasse()}
                style={{
                  padding: "8px 18px",
                  borderRadius: 8,
                  border: "none",
                  background: bulkContrepartiePick && !bulkAffectLoading ? "#f97316" : "#fdba74",
                  color: "white",
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: bulkAffectLoading || !bulkContrepartiePick ? "not-allowed" : "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                {bulkAffectLoading ? <Loader2 size={16} className="animate-spin" style={{ animation: "spin 1s linear infinite" }} /> : null}
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}

      {!loading && totalCount > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 24px",
            borderTop: "1px solid #e2e8f0",
            background: "white",
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: 13, color: "#64748b" }}>
            {startIdx}–{endIdx} sur {totalCount} transaction{totalCount > 1 ? "s" : ""}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 14px",
                borderRadius: 8,
                border: "1px solid #e2e8f0",
                background: safePage <= 1 ? "#f8fafc" : "white",
                color: safePage <= 1 ? "#94a3b8" : "#475569",
                fontSize: 13,
                fontWeight: 600,
                cursor: safePage <= 1 ? "not-allowed" : "pointer",
                fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
              }}
            >
              <ChevronLeft size={18} strokeWidth={2} />
              Précédent
            </button>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#0f172a", minWidth: 80, textAlign: "center" }}>
              Page {safePage} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 14px",
                borderRadius: 8,
                border: "1px solid #e2e8f0",
                background: safePage >= totalPages ? "#f8fafc" : "white",
                color: safePage >= totalPages ? "#94a3b8" : "#475569",
                fontSize: 13,
                fontWeight: 600,
                cursor: safePage >= totalPages ? "not-allowed" : "pointer",
                fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
              }}
            >
              Suivant
              <ChevronRight size={18} strokeWidth={2} />
            </button>
          </div>
        </div>
      )}

      {selectedTx && (
        <TransactionComptaDrawer
          open={!!selectedId}
          onClose={() => setSelectedId(null)}
          transaction={mapToDrawerTransaction(selectedTx, txIdsVente, txIdsAchat)}
          dossierId={activeDossier.id}
          onRapprochement={refresh}
          onPieceUpdated={(patch) => {
            if (!selectedId) return;
            setTransactions((prev) =>
              prev.map((t) => (t.id === selectedId ? { ...t, ...patch } : t))
            );
          }}
          onJustificatifUpdated={(patch) => {
            if (!selectedId) return;
            setTransactions((prev) =>
              prev.map((t) =>
                t.id === selectedId
                  ? {
                      ...t,
                      justificatif_requis: patch.justificatif_requis,
                      justificatif_statut: patch.justificatif_statut,
                      ...(patch.statut_justification !== undefined
                        ? { statut_justification: patch.statut_justification }
                        : {}),
                    }
                  : t
              )
            );
          }}
          onCommentairesChanged={(txId) => {
            if (!activeDossier?.id) return;
            void refreshTransactionsCommentaires([txId]);
          }}
        />
      )}

      {comptaBulkToast ? (
        <div
          role="status"
          style={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 200,
            padding: "12px 20px",
            borderRadius: 12,
            background: comptaBulkToastType === "error" ? "#dc2626" : "#0f172a",
            color: "white",
            fontSize: 14,
            fontWeight: 600,
            boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
            fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
            maxWidth: "min(90vw, 420px)",
            textAlign: "center",
          }}
        >
          {comptaBulkToast}
        </div>
      ) : null}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .tx-action-btn { transition: color 0.15s ease; }
        .tx-action-btn.attach:hover { color: #f97316 !important; }
        .tx-action-btn.edit:hover { color: #3b82f6 !important; }
        .tx-action-btn.delete:hover { color: #dc2626 !important; }
      `}</style>
    </div>
  );
}
